import { Subject } from 'rxjs';
import { GameServices } from '../../services/game-services';
import { CardViewModel } from '../cards/card-view-model';
import { Blackjack } from 'game-logic';
import { delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';

export class PlayerCardStackViewModel {
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get cardViewModelChanges$() {
    return this.cardViewModelChanges.asObservable();
  }

  private cardViewModels: Map<number, CardViewModel> = new Map();
  private readonly cardViewModelChanges = new Subject<{
    type: 'add' | 'addinstant' | 'transfer' | 'remove';
    viewModel: CardViewModel;
    delay: number;
  }>();

  constructor(
    private gameServices: GameServices,
    readonly playerId: Blackjack.PileId,
  ) {}

  public resetGameScene() {
    this.clearAllPlayerCards();
  }

  public async drawDeckPlayer(
    player: Blackjack.Player,
    cardIndex: number | undefined,
  ): Promise<void> {
    for (const [itemIndex, card] of player.allFaceCards.entries()) {
      if (card.index === cardIndex) {
        await this.stackCard(card, itemIndex, player.playerId);
      }
    }
  }

  public async clearAllPlayerCards(): Promise<void> {
    if (this.cardViewModels.size === 0) {
      return;
    }

    const delayMs = UxMetrics.CardClearDelay / this.cardViewModels.size;
    for (const cardViewModel of Array.from(this.cardViewModels.values()).slice().reverse()) {
      this.cardViewModelChanges.next({
        type: 'remove',
        viewModel: cardViewModel,
        delay: UxMetrics.CardClearDelay,
      });
      await delay(delayMs);
    }
    this.cardViewModels.clear();
  }

  public async transferCardToCurrent(cardIndex: number | undefined): Promise<void> {
    if (cardIndex === undefined) {
      return;
    }

    const cardViewModel = this.cardViewModels.get(cardIndex);
    if (cardViewModel) {
      this.cardViewModelChanges.next({
        type: 'transfer',
        viewModel: cardViewModel,
        delay: UxMetrics.CardTransferDelay,
      });
      await delay(UxMetrics.CardTransferDelay);
    }
  }

  public addCard(player: Blackjack.Player, cardIndex: number | undefined): void {
    for (const [itemIndex, card] of player.allFaceCards.entries()) {
      if (card.index === cardIndex) {
        const cardViewModel = new CardViewModel(
          this.gameServices,
          card,
          itemIndex,
          player.playerId,
        );
        cardViewModel.refresh();

        this.cardViewModels.set(cardViewModel.cardIndex, cardViewModel);
        this.cardViewModelChanges.next({
          type: 'addinstant',
          viewModel: cardViewModel,
          delay: 0,
        });
      }
    }
  }

  public removeCard(cardIndex: number | undefined): void {
    if (cardIndex === undefined) {
      return;
    }

    const removedItem = this.cardViewModels.get(cardIndex);
    if (removedItem) {
      this.cardViewModelChanges.next({
        type: 'remove',
        viewModel: removedItem,
        delay: 0,
      });
      this.cardViewModels.delete(cardIndex);
    }
  }

  private async stackCard(
    card: Blackjack.Card,
    index: number,
    playerId: Blackjack.PileId,
  ): Promise<void> {
    const cardViewModel = new CardViewModel(this.gameServices, card, index, playerId);
    cardViewModel.refresh();

    const delayMs = UxMetrics.CardOpenDelay;
    this.cardViewModels.set(cardViewModel.cardIndex, cardViewModel);
    this.cardViewModelChanges.next({
      type: 'add',
      viewModel: cardViewModel,
      delay: delayMs,
    });
    await delay(delayMs);
  }
}
